import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { lawTopicsEN, lawTopicsFR } from '@/i18n/lawTopics'

/**
 * Composable for translating law topics based on the current locale
 *
 * :return: Object containing translateLawTopic function
 */
export function useLawTopicTranslation() {
  const { locale } = useI18n()

  /**
   * Translate a law topic based on the current locale
   * Falls back to German key if no translation exists
   *
   * :param lawTopic: The law topic key to translate (e.g., 'Kündigung')
   * :return: Translated law topic or original German key if no translation exists
   */
  const translateLawTopic = (lawTopic: string | null | undefined): string => {
    if (!lawTopic) return ''

    const currentLocale = locale.value

    if (currentLocale === 'fr') {
      return lawTopicsFR[lawTopic] || lawTopic
    }

    if (currentLocale === 'en') {
      return lawTopicsEN[lawTopic] || lawTopic
    }

    return lawTopic
  }

  /**
   * Computed property that returns translations map based on locale
   * Useful for reactive translations in computed properties
   */
  const lawTopicTranslations = computed(() => {
    if (locale.value === 'fr') return lawTopicsFR
    if (locale.value === 'en') return lawTopicsEN
    return {}
  })

  return {
    translateLawTopic,
    lawTopicTranslations
  }
}
